import { Deferred } from "@lib/utils/deferred";
import { WaveformData } from "../types";
import { CURRENT_WAVEFORM_VERSION } from "../calculateWaveform";
import { MainThreadMessage } from "./InputProcessor.worklet";
import { AudioReader, createAudioRingBuffer, createRingBufferStorage, deinterleave } from "../ring-buffer/audioqueue";

export type ChannelMode = 'left' | 'right' | 'stereo';

/**
 * Level data from the input processor.
 */
export interface LevelData {
  left: number;
  right: number;
}

/**
 * Result returned when recording stops.
 */
export interface RecordingResult {
  /** The recorded audio as an AudioBuffer */
  audioBuffer: AudioBuffer;
  /** Waveform data for visualization */
  waveform: WaveformData;
}

// Step size for waveform data (~50ms worth of samples)
const WAVEFORM_STEP_MS = 50;

// Ring buffer duration in seconds (provides headroom for main thread hiccups)
const RING_BUFFER_DURATION_SECONDS = 4;

// How often to poll the ring buffer (ms) - ~60fps
const POLL_INTERVAL_MS = 16;

// Size of read buffer for polling (interleaved samples per read)
// Read up to 1024 frames at a time
const READ_BUFFER_FRAMES = 1024;

/**
 * AudioWorklet-based input processor node.
 *
 * Provides:
 * - Audio pass-through (connect output to destination for monitoring)
 * - Level metering (realtime L/R peak levels at ~60fps)
 * - PCM recording via SharedArrayBuffer ring buffer
 *
 * Uses a lock-free ring buffer for recording, which provides:
 * - Zero-copy audio transfer from worklet via SharedArrayBuffer
 * - No postMessage overhead for audio data during recording
 * - Predictable memory usage (fixed buffer size)
 * - Real-time waveform computation on main thread as data arrives
 *
 * @example
 * ```ts
 * // Register the worklet module (once per AudioContext)
 * await InputProcessorNode.addModule(audioContext);
 *
 * // Create the processor
 * const processor = new InputProcessorNode(audioContext, (levels) => {
 *   console.log('Levels:', levels.left, levels.right);
 * });
 *
 * // Connect input source to processor
 * sourceNode.connect(processor.node);
 *
 * // Connect output to destination for audio monitoring
 * processor.node.connect(monitorGain).connect(audioContext.destination);
 *
 * // Start recording when needed
 * processor.startRecording('stereo', (waveform) => {
 *   // Update UI with realtime waveform
 * });
 *
 * // Stop and get the recorded audio
 * const { audioBuffer, waveform } = await processor.stopRecording();
 * ```
 */
/**
 * Result returned when recording starts, with timing information.
 */
export interface RecordingStartResult {
  /** The frame number when recording actually started in the worklet */
  startFrame: number;
}

export class InputProcessorNode {
  /**
   * Register the worklet module with the AudioContext.
   * Must be called before creating InputProcessorNode instances.
   */
  static async addModule(audioContext: AudioContext): Promise<void> {
    // The new URL() pattern is transformed by AudioWorkletPlugin at build time
    // to point to the bundled worklet at /_next/static/worklets/input-processor.js
    await audioContext.audioWorklet.addModule(
      new URL('./InputProcessor.worklet.ts', import.meta.url)
    );
  }

  private audioContext: AudioContext;
  private workletNode: AudioWorkletNode;
  private recording = false;
  private startDeferred: Deferred<RecordingStartResult> | null = null;
  private stopDeferred: Deferred<RecordingResult> | null = null;
  private onWaveformUpdate: ((waveform: WaveformData) => void) | null = null;
  private onLevelUpdate: ((levels: LevelData) => void) | null = null;

  // Ring buffer state
  private ringBufferSab: SharedArrayBuffer | null = null;
  private audioReader: AudioReader | null = null;
  private recordingChannelCount = 2;
  private pollIntervalId: ReturnType<typeof setInterval> | null = null;

  // Read buffer for polling (reused to avoid allocations)
  private readBuffer: Float32Array | null = null;
  private deinterleavedBuffers: Float32Array[] | null = null;

  // Accumulated recording data
  private recordedChunks: Float32Array[][] = []; // Array of [left, right] or [mono] chunks

  // Waveform computation state
  private waveformStep: number;
  private waveformData: number[][] = [];
  private waveformMin = 1.0;
  private waveformMax = -1.0;
  private samplesInWaveformSegment = 0;

  // Throttle waveform updates to avoid render cascade
  private lastWaveformNotifyTime = 0;
  private readonly waveformNotifyIntervalMs = 250;

  /**
   * Create a new input processor node.
   *
   * @param audioContext - The AudioContext to use
   * @param onLevelUpdate - Callback for level updates (called at ~60fps)
   */
  constructor(audioContext: AudioContext, onLevelUpdate?: (levels: LevelData) => void) {
    this.audioContext = audioContext;
    this.onLevelUpdate = onLevelUpdate ?? null;

    // Calculate waveform step based on sample rate (~50ms worth of samples)
    this.waveformStep = Math.round(audioContext.sampleRate * WAVEFORM_STEP_MS / 1000);

    this.workletNode = new AudioWorkletNode(audioContext, 'input-processor', {
      numberOfInputs: 1,
      numberOfOutputs: 1, // Output for audio monitoring
      channelCount: 2,
      channelCountMode: 'explicit',
    });

    this.workletNode.port.onmessage = this.handleMessage;
  }

  /**
   * The underlying AudioWorkletNode.
   * Connect your audio source to this node's input.
   * Connect this node's output to destination for audio monitoring.
   */
  get node(): AudioWorkletNode {
    return this.workletNode;
  }

  /**
   * Whether recording is currently active.
   */
  get isRecording(): boolean {
    return this.recording;
  }

  /**
   * Start recording.
   *
   * Returns a promise that resolves when the worklet confirms recording has started,
   * providing the exact frame number when capture began.
   *
   * @param channelMode - Which channel(s) to record: 'left', 'right', or 'stereo'
   * @param onWaveformUpdate - Optional callback for realtime waveform updates
   * @returns Promise that resolves with the recording start timing information
   */
  startRecording(channelMode: ChannelMode, onWaveformUpdate?: (waveform: WaveformData) => void): Promise<RecordingStartResult> {
    if (this.recording) {
      throw new Error('Already recording');
    }

    this.recording = true;
    this.onWaveformUpdate = onWaveformUpdate ?? null;
    this.lastWaveformNotifyTime = performance.now();
    this.startDeferred = new Deferred<RecordingStartResult>();
    this.stopDeferred = new Deferred<RecordingResult>();

    // Reset recording state
    this.recordedChunks = [];
    this.waveformData = [];
    this.waveformMin = 1.0;
    this.waveformMax = -1.0;
    this.samplesInWaveformSegment = 0;

    // Determine channel count
    this.recordingChannelCount = channelMode === 'stereo' ? 2 : 1;

    // Create SharedArrayBuffer for ring buffer
    const ringBufferFrames = RING_BUFFER_DURATION_SECONDS * this.audioContext.sampleRate;
    const ringBufferSamples = ringBufferFrames * this.recordingChannelCount;
    this.ringBufferSab = createRingBufferStorage(ringBufferSamples);

    // Create reader on main thread
    const ringBuffer = createAudioRingBuffer(this.ringBufferSab);
    this.audioReader = new AudioReader(ringBuffer);

    // Allocate read buffers
    this.readBuffer = new Float32Array(READ_BUFFER_FRAMES * this.recordingChannelCount);
    this.deinterleavedBuffers = Array.from(
      { length: this.recordingChannelCount },
      () => new Float32Array(READ_BUFFER_FRAMES)
    );

    // Start polling the ring buffer
    this.startPolling();

    // Send start message with SharedArrayBuffer to worklet
    this.workletNode.port.postMessage({
      type: 'startRecording',
      channelMode,
      ringBufferSab: this.ringBufferSab,
    });

    return this.startDeferred.promise;
  }

  /**
   * Stop recording and return the recorded audio.
   */
  async stopRecording(): Promise<RecordingResult> {
    if (!this.recording || !this.stopDeferred) {
      throw new Error('Not recording');
    }

    this.recording = false;
    this.workletNode.port.postMessage({ type: 'stopRecording' });

    return this.stopDeferred.promise;
  }

  /**
   * Cancel recording without returning data.
   */
  cancelRecording(): void {
    if (!this.recording) return;

    this.recording = false;
    this.stopPolling();
    this.workletNode.port.postMessage({ type: 'stopRecording' });
    this.startDeferred = null;
    this.stopDeferred = null;
    this.cleanupRecordingState();
  }

  /**
   * Disconnect and clean up the node.
   */
  destroy(): void {
    this.cancelRecording();
    this.onLevelUpdate = null;
    this.workletNode.disconnect();
  }

  private startPolling(): void {
    this.pollIntervalId = setInterval(() => this.pollRingBuffer(), POLL_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollIntervalId !== null) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
  }

  private pollRingBuffer(): void {
    if (!this.audioReader || !this.readBuffer || !this.deinterleavedBuffers) return;

    // Read all available data from ring buffer
    while (true) {
      const available = this.audioReader.availableRead();
      if (available === 0) break;

      // Read up to readBuffer size
      const toRead = Math.min(available, this.readBuffer.length);
      const read = this.audioReader.dequeue(this.readBuffer, toRead);
      if (read === 0) break;

      // Deinterleave and store
      const framesRead = read / this.recordingChannelCount;
      deinterleave(
        this.readBuffer.subarray(0, read),
        this.deinterleavedBuffers,
        framesRead
      );

      // Copy to chunks (need to slice since we reuse the buffers)
      const chunk: Float32Array[] = this.deinterleavedBuffers.map(
        buf => buf.slice(0, framesRead)
      );
      this.recordedChunks.push(chunk);

      // Update waveform (use first channel for waveform visualization)
      this.updateWaveform(chunk[0]);
    }
  }

  private updateWaveform(samples: Float32Array): void {
    // Track min/max for waveform visualization
    for (let i = 0; i < samples.length; i++) {
      const sample = samples[i];
      if (sample < this.waveformMin) this.waveformMin = sample;
      if (sample > this.waveformMax) this.waveformMax = sample;
    }

    this.samplesInWaveformSegment += samples.length;

    // Emit waveform segment every waveformStep samples (~50ms)
    while (this.samplesInWaveformSegment >= this.waveformStep) {
      this.waveformData.push([this.waveformMin, this.waveformMax]);

      // Reset for next segment
      this.waveformMin = 1.0;
      this.waveformMax = -1.0;
      this.samplesInWaveformSegment -= this.waveformStep;
    }

    // Throttle UI updates
    const now = performance.now();
    if (this.onWaveformUpdate && now - this.lastWaveformNotifyTime >= this.waveformNotifyIntervalMs) {
      this.lastWaveformNotifyTime = now;
      this.onWaveformUpdate(this.getWaveform());
    }
  }

  private cleanupRecordingState(): void {
    this.ringBufferSab = null;
    this.audioReader = null;
    this.readBuffer = null;
    this.deinterleavedBuffers = null;
    this.recordedChunks = [];
    this.waveformData = [];
    this.onWaveformUpdate = null;
  }

  private handleMessage = (event: MessageEvent<MainThreadMessage>): void => {
    const message = event.data;

    if (message.type === 'level') {
      this.onLevelUpdate?.(message);
    } else if (message.type === 'recordingStarted') {
      this.handleRecordingStarted(message.currentFrame);
    } else if (message.type === 'stopped') {
      this.handleStopped(message.totalSamplesWritten);
    } else if (message.type === 'bufferOverflow') {
      console.warn(`Ring buffer overflow: ${message.droppedSamples} samples dropped`);
    }
  };

  private handleRecordingStarted(currentFrame: number): void {
    if (!this.startDeferred) return;

    this.startDeferred.resolve({ startFrame: currentFrame });
    this.startDeferred = null;
  }

  private handleStopped(_totalSamplesWritten: number): void {
    if (!this.stopDeferred) return;

    // Stop polling and do a final read
    this.stopPolling();
    this.pollRingBuffer(); // Final drain of any remaining samples

    // Concatenate all chunks into AudioBuffer
    const audioBuffer = this.createAudioBuffer();

    this.stopDeferred.resolve({
      audioBuffer,
      waveform: this.getWaveform(),
    });

    // Clean up
    this.stopDeferred = null;
    this.cleanupRecordingState();
  }

  private createAudioBuffer(): AudioBuffer {
    // Calculate total frames
    let totalFrames = 0;
    for (const chunk of this.recordedChunks) {
      totalFrames += chunk[0].length;
    }

    if (totalFrames === 0) {
      // Empty recording - create minimal buffer
      return this.audioContext.createBuffer(1, 1, this.audioContext.sampleRate);
    }

    const audioBuffer = this.audioContext.createBuffer(
      this.recordingChannelCount,
      totalFrames,
      this.audioContext.sampleRate
    );

    // Copy chunks into AudioBuffer channels
    for (let ch = 0; ch < this.recordingChannelCount; ch++) {
      const channelData = audioBuffer.getChannelData(ch);
      let offset = 0;
      for (const chunk of this.recordedChunks) {
        channelData.set(chunk[ch], offset);
        offset += chunk[ch].length;
      }
    }

    return audioBuffer;
  }

  private getWaveform(): WaveformData {
    return {
      step: this.waveformStep,
      version: CURRENT_WAVEFORM_VERSION,
      data: [...this.waveformData],
    };
  }
}
